import {Button, ButtonGroup} from '@mui/material';
import update from 'immutability-helper';
import isEmpty from 'lodash/isEmpty';
import {useMemo} from 'react';

import {DashboardPanelData} from '../../../interfaces/Dashboard';
import {
  injectDashboardDates,
  useDashboardDates,
} from '../../../utils/dashboardDate';
import {DashboardPanelActionsSlot} from '../DashboardPanelActionsSlot';
import {
  AssetTrackingReports,
  AssetTrackingReportsData,
  getAssetTrackingReportsData,
} from './AssetTrackingReports';
import {
  EmployeeTrackingReports,
  EmployeeTrackingReportsData,
  getEmployeeTrackingReportsData,
} from './EmployeeTrackingReports';

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value?: DashboardPanelData) => void;
}

export interface CommtracNodeTrackingReportsData {
  viewType: 'employees' | 'assets';
  title: string;
  employees: EmployeeTrackingReportsData;
  assets: AssetTrackingReportsData;
}

const getCommtracNodeTrackingReports = (): CommtracNodeTrackingReportsData => ({
  viewType: 'employees',
  title: 'Communication Nodes Tracking Reports',
  employees: getEmployeeTrackingReportsData(),
  assets: getAssetTrackingReportsData(),
});

export const CommtracNodeTrackingReports = ({value, onUpdate}: Props) => {
  const config = useMemo(
    () =>
      !isEmpty(value)
        ? (value as CommtracNodeTrackingReportsData)
        : getCommtracNodeTrackingReports(),
    [value]
  );

  const dashboardDates = useDashboardDates(value?.detachedFromDashboardDate);

  const assetsValue = useMemo(() => {
    const base = config.assets;
    if (!dashboardDates) {
      return base;
    }
    const newItems = injectDashboardDates(base.openedItems, dashboardDates);
    if (newItems === base.openedItems) {
      return base;
    }
    return {...base, openedItems: newItems};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.assets, dashboardDates?.date_start, dashboardDates?.date_end]);

  const employeesValue = useMemo(() => {
    const base = config.employees;
    if (!dashboardDates) {
      return base;
    }
    const newItems = injectDashboardDates(base.openedItems, dashboardDates);
    if (newItems === base.openedItems) {
      return base;
    }
    return {...base, openedItems: newItems};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.employees, dashboardDates?.date_start, dashboardDates?.date_end]);

  return (
    <>
      <DashboardPanelActionsSlot>
        <ButtonGroup sx={{minWidth: 180}}>
          <Button
            size="small"
            variant={config.viewType === 'employees' ? 'contained' : 'outlined'}
            onClick={() => {
              onUpdate?.(
                update(value ?? getCommtracNodeTrackingReports(), {
                  viewType: {
                    $set: 'employees',
                  },
                })
              );
            }}
          >
            Employees
          </Button>

          <Button
            size="small"
            variant={config.viewType === 'assets' ? 'contained' : 'outlined'}
            onClick={() => {
              onUpdate?.(
                update(value ?? getCommtracNodeTrackingReports(), {
                  viewType: {
                    $set: 'assets',
                  },
                })
              );
            }}
          >
            Assets
          </Button>
        </ButtonGroup>
      </DashboardPanelActionsSlot>

      {config?.viewType === 'assets' ? (
        <AssetTrackingReports
          value={assetsValue}
          onUpdate={(v) => {
            let detach = false;
            if (
              dashboardDates &&
              v?.openedItems &&
              v.openedItems.length === config.assets.openedItems.length
            ) {
              const activeIdx = v.openedItems.findIndex(
                (it) => it.id === (v.activeId ?? config.assets.activeId)
              );
              const incoming =
                activeIdx >= 0 ? v.openedItems[activeIdx] : undefined;
              if (
                incoming &&
                (incoming.params?.date_start !== dashboardDates.date_start ||
                  incoming.params?.date_end !== dashboardDates.date_end)
              ) {
                detach = true;
              }
            }
            const base = update(value ?? getCommtracNodeTrackingReports(), {
              assets: {$set: v ?? getAssetTrackingReportsData()},
              employees: {
                $set: value?.employees ?? getEmployeeTrackingReportsData(),
              },
              viewType: {$set: 'assets'},
            });
            onUpdate?.(
              detach ? {...base, detachedFromDashboardDate: true} : base
            );
          }}
        />
      ) : config?.viewType === 'employees' ? (
        <EmployeeTrackingReports
          value={employeesValue}
          onUpdate={(v) => {
            let detach = false;
            if (
              dashboardDates &&
              v?.openedItems &&
              v.openedItems.length === config.employees.openedItems.length
            ) {
              const activeIdx = v.openedItems.findIndex(
                (it) => it.id === (v.activeId ?? config.employees.activeId)
              );
              const incoming =
                activeIdx >= 0 ? v.openedItems[activeIdx] : undefined;
              if (
                incoming &&
                (incoming.params?.date_start !== dashboardDates.date_start ||
                  incoming.params?.date_end !== dashboardDates.date_end)
              ) {
                detach = true;
              }
            }
            const base = update(value ?? getCommtracNodeTrackingReports(), {
              employees: {$set: v ?? getEmployeeTrackingReportsData()},
              assets: {$set: value?.assets ?? getAssetTrackingReportsData()},
              viewType: {$set: 'employees'},
            });
            onUpdate?.(
              detach ? {...base, detachedFromDashboardDate: true} : base
            );
          }}
        />
      ) : null}
    </>
  );
};
